import "./SubscriptionFreePageV2.scss";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { ceoLetterState, memberState, mobileState } from "../../../state/atoms";
import ProStarFilledIcon from "../../icons/ProStarFilledIcon";
import DesignPlanIcon from "../../icons/DesignPlanIcon";
import {
  DESIGN_FEATURES,
  FINAL_DATE,
  FORMATTED_FINAL_DATE,
  PRO_FEATURES
} from "../../../util/constants";
import { PlanLength, PlanType } from "../../../util/types";
import { useState } from "react";
import { getPlanDescription, getPlanPrice, getPlanSubtitle } from "../../../service/SubscriptionService";
import { Button } from "../misc/Button";
import Checkmark from "../../icons/Checkmark";
import PlanLengthSwitch from "../../PlanLengthSwitch";
import WarningIcon from "../../icons/WarningIcon";
import { daysUntil } from "../../../util/utils";
import { Modal, Tabs, TabsProps } from "antd";
import DesignPlanSummary from "../../DesignPlanSummary";

export function SubscriptionFreePageV2() {
  const [planLength, setPlanLength] = useState<PlanLength>("annual");
  const [planType, setPlanType] = useState<PlanType>("pro");
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const member = useRecoilValue(memberState);
  const mobile = useRecoilValue(mobileState);
  const setSeeCeoLetter = useSetRecoilState(ceoLetterState);

  function onGetProClick() {
    if (!member || !member.id) return;
    setShowLearnMoreModal(false);
    window.MHM.ProModal.upgrade({
      memberId: member.id,
      onSuccess: () => window.location.reload(),
    });
  }

  function onGetDesignClick() {
    if (!member || !member.id) return;
    setShowLearnMoreModal(false);
    window.MHM.ProModal.upgradeToDesign({
      memberId: member.id,
      onSuccess: () => window.location.reload(),
    });
  }

  const items: TabsProps["items"] = [
    {
      key: "design",
      label: "Design"
    },
    {
      key: "pro",
      label: "Pro"
    }
  ];

  function onSeeCeoLetter() {
    setShowLearnMoreModal(false);
    setSeeCeoLetter(true);
  }

  const header = (planType: PlanType) => (
    <header>
      <h1>
        {planType === "design" ? <DesignPlanIcon /> : <ProStarFilledIcon />}
        <span>{planType === "design" ? "Design" : "Pro"}</span>
      </h1>

      <div>
        <span className={"price"}>
          {getPlanPrice(planType, planLength)}
        </span>
        <span className={"subtitle"}>
          {getPlanSubtitle(planType, planLength)}
        </span>
        <span className={"description"}>
          {getPlanDescription(planType, planLength)}
        </span>
      </div>
      <div>
        <Button size={"Large"} className={`primary ${planType === "design" ? "design" : ""}`}
                onClick={planType === "design" ? onGetDesignClick : onGetProClick}>{planType === "design" ? "Get Design Plan" : "Get Pro"}</Button>
      </div>
    </header>
  );

  return (
    <div className={"free-subscription-page"}>
      <div className={"plan-discontinued-notice"}>
        <div>
          <WarningIcon />
          <span>In {daysUntil(FINAL_DATE)} Days Our Free Plan will be discontinued.</span>
        </div>
        <div>
          <span className={"link"} onClick={() => setShowLearnMoreModal(true)}>Learn more</span>
        </div>
      </div>

      <div className={"plan-switch-wrapper"}>
        <PlanLengthSwitch planLength={planLength} setPlanLength={setPlanLength} />
      </div>

      {mobile && <Tabs className={"tabs"} activeKey={planType} onChange={(k) => setPlanType(k as PlanType)} items={items} />}

      <div className={"plans"}>
        {(!mobile || planType === 'design') && <div>
          {header("design")}
          <section>
            <h3>The #1 Menu Creator:</h3>
            <div className={"design-features"}>
              {DESIGN_FEATURES.map((feature) => (
                <div key={feature}>
                  <div><Checkmark /></div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className={"printing"}>
              <h3>Expert restaurant printing:</h3>
              <span>Letter, legal and tabloid menus, lamination, synthetic paper, table tents, trifold takeout, bifold menus, posters, flyers, sandwich boards, loyalty cards, stickers, business cards and more</span>
            </div>

            <div className={"learn-more-wrapper"}>
              <a href={"/menu/pricing.do"} className={"learn-more"} target={'_blank'} rel="noreferrer">Learn More</a>
            </div>
          </section>
        </div>}

        {(!mobile || planType === 'pro') && <div>
          {header("pro")}
          <section>
            <h3>Everything Design has, plus:</h3>
            <div className={"design-features"}>
              {PRO_FEATURES.map((feature) => (
                <div key={feature}>
                  <div><Checkmark /></div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </section>
        </div>}
      </div>
      <Modal className={"learn-more-modal clean"} open={showLearnMoreModal} onCancel={() => setShowLearnMoreModal(false)} footer={null}>
        <header>
          <h2>
            After 10 great years, we are discontinuing our Free Plan. In its place, we are introducing the new base-level Design Plan. Please upgrade
            to maintain your access.
          </h2>
          <h3>{`The final day of free is set to be ${FORMATTED_FINAL_DATE}. `}</h3><h3 className={'see-ceo-letter'} onClick={onSeeCeoLetter}>See CEO letter</h3>
        </header>

        <DesignPlanSummary/>

        <footer>
          <Button size={"Large"} className={`primary design`} onClick={onGetDesignClick}>Get Design Plan</Button>
        </footer>
      </Modal>
    </div>
  );
}
